import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormControl, InputLabel, MenuItem, Select } from '@mui/material';
import PuffLoader from 'react-spinners/PuffLoader';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { useReactToPrint } from 'react-to-print';
import ReportsService from '../../../Services/reports.service';
import YearlyVatComparisonTable from './YearlyVatComparisonTable';
import YearlyVatManualEntry from './YearlyVatManualEntry';
import YearlyVatReportImport from './YearlyVatReportImport';
import {
    buildManualRows,
    isRowDirty,
    periodMatchKey,
    toBulkDeletePayload,
    toBulkPeriodPayload,
    yearOptions,
} from './yearlyVatReportUtils';
import './YearlyVatReport.css';

const TABS = {
    comparison: 'comparison',
    manual: 'manual',
};

/**
 * Standalone yearly VAT report: computed (מחושב) vs reported (מדווח) per period, printable and
 * downloadable as PDF, with manual reporting for several periods at once and auto-fill from a
 * Tax Authority VAT summary report.
 *
 * Rendered without a modal so it can be embedded in a page, a drawer or `YearlyVatReportModal`.
 */
const YearlyVatReport = ({
    caseId,
    client,
    token,
    /** Any date inside the year to show; defaults to today. */
    date,
    /** Hidden when the component is embedded rather than shown in a modal. */
    onClose,
    /** Called after reported VAT was saved, so the host can refresh its own data. */
    onDataUpdate,
    showManualReporting = true,
    showImport = true,
}) => {
    const { t, i18n } = useTranslation();
    const direction = i18n.language === 'he' || i18n.language === 'ar' ? 'rtl' : 'ltr';

    const initialDate = useMemo(() => (date ? new Date(date) : new Date()), [date]);
    const [year, setYear] = useState(initialDate.getFullYear());
    const [activeTab, setActiveTab] = useState(TABS.comparison);
    const [data, setData] = useState(null);
    const [manualRows, setManualRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [loadError, setLoadError] = useState(null);
    const [saveErrors, setSaveErrors] = useState([]);

    const printAreaRef = useRef(null);

    /** The month the server should stop at: December for past years, the current month for this year. */
    const reportDate = useMemo(() => {
        const now = new Date();
        const month = year === now.getFullYear() ? now.getMonth() + 1 : 12;
        return new Date(Date.UTC(year, month - 1, 1)).toISOString();
    }, [year]);

    const loadData = useCallback(async () => {
        if (!caseId || !token) return;

        setLoading(true);
        setLoadError(null);

        try {
            const result = await ReportsService.getYearlyVatReportData(caseId, reportDate, token);
            setData(result);
            setManualRows(buildManualRows(result));
        } catch (error) {
            setData(null);
            setManualRows([]);
            setLoadError(error?.message || t('FailedToLoadYearlyVatReport'));
        } finally {
            setLoading(false);
        }
    }, [caseId, token, reportDate, t]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const handlePrint = useReactToPrint({
        content: () => printAreaRef.current,
        documentTitle: `${t('ComparativeVATReport')} ${year} - ${caseId}`,
    });

    const handleDownloadPdf = async () => {
        setDownloading(true);
        try {
            await ReportsService.getYearlyVatReport(caseId, reportDate, token);
            toast.success(t('YearlyVATReportIsDownloading'));
        } catch (error) {
            toast.error(t('FailedToDownloadYearlyVATReport'));
        } finally {
            setDownloading(false);
        }
    };

    const updateRow = (periodKey, updater) => {
        setManualRows((rows) => rows.map((row) => (row.periodKey === periodKey ? updater(row) : row)));
    };

    const handleChangeValue = (periodKey, field, value) => {
        updateRow(periodKey, (row) => ({
            ...row,
            selected: true,
            values: { ...row.values, [field]: value },
        }));
    };

    const handleToggleRow = (periodKey, selected) => {
        updateRow(periodKey, (row) => ({ ...row, selected }));
    };

    const handleToggleAll = (selected) => {
        setManualRows((rows) => rows.map((row) => ({ ...row, selected })));
    };

    /** Fills the editable grid from a parsed Tax Authority report; returns how many rows changed. */
    const handlePeriodsParsed = (result) => {
        const parsedByKey = new Map(
            (result?.periods ?? [])
                .filter((period) => !period.isEmpty)
                .map((period) => [periodMatchKey(period.startDate), period])
        );

        setManualRows((rows) =>
            rows.map((row) => {
                const parsed = parsedByKey.get(row.matchKey);
                if (!parsed) return row;

                return {
                    ...row,
                    selected: true,
                    filledFromDocument: true,
                    values: {
                        incomesNoZeroVats: parsed.incomesNoZeroVats,
                        incomesNoVat: parsed.incomesNoVat,
                        vatTransactions: parsed.vatTransactions,
                        equipmentVAT: parsed.equipmentVAT,
                        otherVATTaxes: parsed.otherVATTaxes,
                    },
                };
            })
        );

        setActiveTab(TABS.manual);
        return manualRows.filter((row) => parsedByKey.has(row.matchKey)).length;
    };

    const selectedRows = manualRows.filter((row) => row.selected);

    const handleSave = async (asDelete = false) => {
        if (selectedRows.length === 0) {
            toast.info(t('SelectAtLeastOnePeriod'));
            return;
        }

        setSaving(true);
        setSaveErrors([]);

        try {
            const periods = selectedRows.map(asDelete ? toBulkDeletePayload : toBulkPeriodPayload);
            const result = await ReportsService.createReportedVatBulk(caseId, periods, token);

            const failed = (result?.results ?? []).filter((periodResult) => !periodResult.success);
            setSaveErrors(failed.map((periodResult) => `${periodResult.periodKey}: ${periodResult.error}`));

            if (result?.success) {
                toast.success(
                    asDelete
                        ? t('ReportedVatDeletedSuccess')
                        : t('ReportedVatPeriodsSavedSuccess', { count: result.savedCount })
                );
            } else {
                toast.warn(t('SomeReportedVatPeriodsFailed', { count: failed.length }));
            }

            await loadData();
            onDataUpdate?.();
        } catch (error) {
            setSaveErrors([error?.message || t('ReportedTaxUpdateFailed')]);
            toast.error(t('ReportedTaxUpdateFailed'));
        } finally {
            setSaving(false);
        }
    };

    const dirtyCount = manualRows.filter(isRowDirty).length;

    return (
        <div className="yvr" dir={direction}>
            <div className="yvr__header yvr__no-print">
                <div className="yvr__titles">
                    <h2 className="yvr__title">{t('ComparativeVATReport')}</h2>
                    <span className="yvr__subtitle">
                        {[data?.clientName || client?.name, caseId, data?.reportingType || client?.reportingType]
                            .filter(Boolean)
                            .join(' • ')}
                    </span>
                </div>

                <div className="yvr__header-actions">
                    <FormControl size="small" sx={{ minWidth: 110 }}>
                        <InputLabel id="yvr-year-label">{t('Year')}</InputLabel>
                        <Select
                            labelId="yvr-year-label"
                            label={t('Year')}
                            value={year}
                            onChange={(event) => setYear(Number(event.target.value))}
                        >
                            {yearOptions().map((option) => (
                                <MenuItem key={option} value={option}>{option}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <button type="button" className="yvr__btn" onClick={handlePrint} disabled={!data}>
                        {t('Print')}
                    </button>
                    <button
                        type="button"
                        className="yvr__btn"
                        onClick={handleDownloadPdf}
                        disabled={!data || downloading}
                    >
                        {downloading ? t('Downloading') : t('DownloadPDFFile')}
                    </button>
                    <button type="button" className="yvr__btn" onClick={loadData} disabled={loading}>
                        {t('Refresh')}
                    </button>

                    {onClose && (
                        <button type="button" className="yvr__close-btn" onClick={onClose} aria-label={t('Close')}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 20 20" fill="none">
                                <path d="M15.2501 4.75831C14.9251 4.43331 14.4001 4.43331 14.0751 4.75831L10.0001 8.82498L5.9251 4.74998C5.6001 4.42498 5.0751 4.42498 4.7501 4.74998C4.4251 5.07498 4.4251 5.59998 4.7501 5.92498L8.8251 9.99998L4.7501 14.075C4.4251 14.4 4.4251 14.925 4.7501 15.25C5.0751 15.575 5.6001 15.575 5.9251 15.25L10.0001 11.175L14.0751 15.25C14.4001 15.575 14.9251 15.575 15.2501 15.25C15.5751 14.925 15.5751 14.4 15.2501 14.075L11.1751 9.99998L15.2501 5.92498C15.5668 5.60831 15.5668 5.07498 15.2501 4.75831Z" fill="currentColor" />
                            </svg>
                        </button>
                    )}
                </div>
            </div>

            {showManualReporting && (
                <div className="yvr__toolbar yvr__no-print">
                    <div className="yvr__tabs">
                        <button
                            type="button"
                            className={`yvr__tab ${activeTab === TABS.comparison ? 'yvr__tab--active' : ''}`}
                            onClick={() => setActiveTab(TABS.comparison)}
                        >
                            {t('ComparisonView')}
                        </button>
                        <button
                            type="button"
                            className={`yvr__tab ${activeTab === TABS.manual ? 'yvr__tab--active' : ''}`}
                            onClick={() => setActiveTab(TABS.manual)}
                        >
                            {t('MultiPeriodManualReporting')}
                        </button>
                    </div>

                    {dirtyCount > 0 && (
                        <span className="yvr__manual-hint">{t('UnsavedPeriodsHint', { count: dirtyCount })}</span>
                    )}
                </div>
            )}

            {loadError && <div className="yvr__alert yvr__alert--error yvr__no-print">{loadError}</div>}

            {loading ? (
                <div className="yvr__state">
                    <PuffLoader color="#4f46e5" size={54} />
                    <span>{t('Loading')}</span>
                </div>
            ) : !data || data.periods.length === 0 ? (
                <div className="yvr__state">{t('NoDataForSelectedYear')}</div>
            ) : activeTab === TABS.comparison ? (
                <div className="yvr-print-area" ref={printAreaRef}>
                    <div className="yvr__titles" style={{ marginBottom: 12 }}>
                        <h2 className="yvr__title">{`${t('ComparativeVATReport')} — ${year}`}</h2>
                        <span className="yvr__subtitle">
                            {[data.clientName, caseId, data.reportingType].filter(Boolean).join(' • ')}
                        </span>
                    </div>
                    <YearlyVatComparisonTable t={t} data={data} />
                </div>
            ) : (
                <>
                    {showImport && (
                        <YearlyVatReportImport
                            t={t}
                            caseId={caseId}
                            token={token}
                            year={year}
                            onPeriodsParsed={handlePeriodsParsed}
                        />
                    )}

                    <YearlyVatManualEntry
                        t={t}
                        rows={manualRows}
                        onChangeValue={handleChangeValue}
                        onToggleRow={handleToggleRow}
                        onToggleAll={handleToggleAll}
                    />

                    {saveErrors.length > 0 && (
                        <div className="yvr__alert yvr__alert--error yvr__no-print">
                            {saveErrors.map((message, index) => (
                                <div key={index}>{message}</div>
                            ))}
                        </div>
                    )}

                    <div className="yvr__footer yvr__no-print">
                        <span className="yvr__manual-hint">
                            {t('SelectedPeriodsCount', { count: selectedRows.length })}
                        </span>
                        <div className="yvr__header-actions">
                            <button
                                type="button"
                                className="yvr__btn yvr__btn--danger"
                                onClick={() => handleSave(true)}
                                disabled={saving || selectedRows.length === 0}
                            >
                                {t('MarkSelectedAsUnreported')}
                            </button>
                            <button
                                type="button"
                                className="yvr__btn yvr__btn--primary"
                                onClick={() => handleSave(false)}
                                disabled={saving || selectedRows.length === 0}
                            >
                                {saving ? t('Saving') : t('SaveSelectedPeriods')}
                            </button>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default YearlyVatReport;
